/**
 * 外注見積の詳細の画面（#198 の続き、docs/79 §13・§14。2026-10-17 射場さんの決定 A〜C）。道は /vendor-quotes/:id?<一覧の条件>
 *   一覧の右側パネルと前の「中身の画面」を1つにした。左のメニューは畳んだ 64px で始まる（Route の lineEntry）
 *   頭の帯：1段目＝‹ {vendorQuote}・{vendor}・状態・検算・件名と番号／前へ・次へ・見積で取込・⋯
 *           2段目＝{vendor}・{project}・担当者（選んだ瞬間に保存。版つき）・金額（税抜・消費税・税込）・反映・詳細 ⌄
 *           詳細＝件名（自動保存）・見積番号・見積日・税・税抜合計（手入力のとき）・見積依頼・取込日。要確認の理由はいちばん上に朱の1行
 *   本体：原本（ページの画像＝components/PageViewer）｜境（components/SplitPanes の useSplit・gb.vq.split・既定 50:50）｜AI読み取り結果
 *     結果の表は名称・規格の列を最低 160px（狭いと 区分・単価 → 単位・取込 を隠す）。行を押す（Enter）と修正の帯。手入力のときは行を追加
 *   前へ／次へ：一覧の条件（URL の問い合わせ）と並びのまま。移るときは履歴を置き換える（戻るはいつも一覧）
 *   スマホ（幅 767px 以下・スマホ版）は閲覧のみ（一覧のシートと同じ中身）
 */
import {
  ORDER_BUNDLE_MSG,
  ORDER_MSG,
  VENDOR_LINE_ROLE_LABEL,
  VENDOR_QUOTE_DETAIL_MSG as M,
  VENDOR_QUOTE_LIST_MSG,
  VENDOR_QUOTE_LIST_STATE_LABEL,
  VENDOR_QUOTE_MSG,
  vendorQuoteStateLabel,
  type GroupList,
  type Order,
  type PartnerList,
  type VendorQuote,
  type VendorQuoteAdoption,
  type VendorQuoteLine,
  type VendorQuoteListFilters,
  type VendorQuoteNeighbors,
  type VendorQuotePages,
  type VendorQuoteRow,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { ApiError, api, messageOf } from "../../api.js";
import { useMe } from "../../auth.js";
import { BackButton, useBackNav, usePageName } from "../../components/BackButton.jsx";
import { docShort } from "../../components/AreaIcon.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { DOTS_H } from "../../components/HeadActions.jsx";
import { WidenIcon } from "../../components/icons.jsx";
import { notify } from "../../components/Notice.jsx";
import { NumInput } from "../../components/NumInput.jsx";
import { PageViewer, type Zoom } from "../../components/PageViewer.jsx";
import { RowMenu, type RowMenuItem } from "../../components/RowMenu.jsx";
import { useSplit } from "../../components/SplitPanes.jsx";
import { UnitInput } from "../../components/UnitInput.jsx";
import { ErrorText, Help } from "../../components/ui.jsx";
import { sheetLabel } from "../../components/VendorImport.jsx";
import { t, useLabels } from "../../labels.js";
import { PASSIVE } from "../../lib/autoLogout.js";
import { usePcOnly } from "../../lib/pcOnly.js";
import { useSaveQueue } from "../../lib/saveQueue.jsx";
import { ICON, ReadCell, StateChip, VendorQuoteDeleteDialog, invalidateList, reasonText, receivedTitle, titleOf, yenOrDash } from "./parts.jsx";
import { PhoneDetailBody } from "./PhoneDetail.jsx";
import { AdoptionToggle, AlreadyDialog, CompareBand, DeclineMailDialog } from "../orders/bundle.jsx";
import { md } from "../rfq/parts.jsx";

type GroupJobs = { id: string; jobs: { id: string; subject: string; displayNo: string | null; status: string }[] };
type Saved = VendorQuote & { message?: string };

/** 境（原本の割合 %）：既定 50:50・30〜70・端末に覚える（受け取った書類の gb.rd.split と別。docs/79 §13.5 ア） */
const SPLIT_KEY = "gb.vq.split";
const SPLIT_DEFAULT = 50;
/** 「詳細 ⌄」の開閉を端末に覚える（§13.5 サ。既定は閉じた形） */
const HEAD_KEY = "gb.vq.head";
const readHead = (): boolean => {
  try {
    return localStorage.getItem(HEAD_KEY) === "1";
  } catch {
    return false;
  }
};
/** 結果の表の列を隠す境目（px。§13.5 ユ＝名称は最低 160px）。固定の列は合計 428px（No 36・区分 60・数量 56・単位 44・単価 80・金額 96・取込 56）
 *  ＝588px 未満で 区分・単価（140px）を、450px 未満で 単位・取込（100px）も隠す。区分は「諸経費」「見出し」の3文字が1行に入る 60px（10/17 本番の確かめ） */
const NARROW_PX = 588;
const TIGHT_PX = 450;
/** 見えている列の数（帯・修正の帯の colSpan。隠した列の分まで跨ぐと、table-layout:fixed が見えない列を作って名称の列を潰す＝10/17 本番で 33px） */
const visibleCols = (w: number): number => (w < TIGHT_PX ? 4 : w < NARROW_PX ? 6 : 8);

const SUM = new Set<VendorQuoteLine["role"]>(["subtotal", "total", "tax"]);

export function VendorQuoteDetailPage() {
  const { id = "" } = useParams();
  const pc = usePcOnly();
  if (pc.phone) return <PhoneDetailPage id={id} />;
  return <Detail key={id} id={id} />;
}

/** スマホ：一覧のシートと同じ中身の閲覧のみの画面（§13.3・§13.5 ヒ） */
function PhoneDetailPage({ id }: { id: string }) {
  const L = useLabels();
  const loc = useLocation();
  const back = useBackNav({ to: `/vendor-quotes${loc.search}`, label: L.vendorQuote });
  const q = useQuery({ queryKey: ["vendor-quote", id], queryFn: () => api<VendorQuote>("GET", `/vendor-quotes/${id}`), retry: false });
  usePageName(q.data ? (q.data.row?.vendor?.name ?? q.data.issuerName ?? L.vendorQuote) : null, q.data ? docShort(L.vendorQuote, q.data.quoteNo || null) : null);
  return (
    <div className="vqd-ph" data-testid="vqd-phone">
      <BackButton back={back} />
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : <PhoneDetailBody id={id} page />}
      {back.dialog}
    </div>
  );
}

function Detail({ id }: { id: string }) {
  const L = useLabels();
  const me = useMe();
  const pcOnly = usePcOnly();
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  /** 一覧の条件（URL の問い合わせ）。前へ・次へと戻る先に使う（§13.5 テ） */
  const listSearch = loc.search;
  const listQs = listSearch.replace(/^\?/, "");
  const back = useBackNav({ to: `/vendor-quotes${listSearch}`, label: L.vendorQuote });

  const q = useQuery({
    queryKey: ["vendor-quote", id],
    queryFn: () => api<VendorQuote>("GET", `/vendor-quotes/${id}`),
    retry: false,
    refetchInterval: (x) => (x.state.data?.status === "queued" || x.state.data?.status === "reading" ? 5_000 : false),
  });
  usePageName(q.data ? (q.data.row?.vendor?.name ?? q.data.issuerName ?? L.vendorQuote) : null, q.data ? docShort(L.vendorQuote, q.data.quoteNo || null) : null);
  const pages = useQuery({ queryKey: ["vq-pages", id], queryFn: () => api<VendorQuotePages>("GET", `/vendor-quotes/${id}/pages`), retry: false, gcTime: 0 });
  const nb = useQuery({ queryKey: ["vq-nb", id, listQs], queryFn: () => api<VendorQuoteNeighbors>("GET", `/vendor-quotes/${id}/neighbors${listSearch}`, undefined, PASSIVE), retry: false });
  const vendors = useQuery({ queryKey: ["partners", "", "active", "vendor"], queryFn: () => api<PartnerList>("GET", "/partners?type=vendor") });
  const groups = useQuery({ queryKey: ["groups", "all"], queryFn: () => api<GroupList>("GET", "/groups?state=all") });
  const filters = useQuery({ queryKey: ["vql-filters"], queryFn: () => api<VendorQuoteListFilters>("GET", "/vendor-quotes/filters") });
  const groupId = q.data?.group?.id ?? "";
  const group = useQuery({ queryKey: ["group", groupId], queryFn: () => api<GroupJobs>("GET", `/groups/${groupId}`), enabled: !!groupId });

  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState<"delete" | "decline" | null>(null);
  const [open, setOpenRaw] = useState(readHead);
  const setOpen = (v: boolean) => {
    setOpenRaw(v);
    try {
      localStorage.setItem(HEAD_KEY, v ? "1" : "0");
    } catch {
      /* 端末に覚えられなくても動く */
    }
  };
  const [zoom, setZoom] = useState<Zoom>("fit");
  const [jump, setJump] = useState<{ n: number; at: number } | null>(null);
  const sp = useSplit({ storageKey: SPLIT_KEY, def: SPLIT_DEFAULT });
  const row = q.data?.row ?? null;
  const rowRef = useRef<VendorQuoteRow | null>(row);
  rowRef.current = row;

  // 読み取り・検算の結果は通知で1回（#168。この画面を開いたとき・読み取りが終わったとき）。状態は頭の帯のチップに残る
  const checked = useRef<string | null>(null);
  const cr = q.data?.check.result;
  const st = q.data?.status;
  useEffect(() => {
    if (!q.data || checked.current === id || (cr !== "ok" && cr !== "mismatch") || st === "queued" || st === "reading" || st === "failed") return;
    checked.current = id;
    const note = q.data.check.note;
    notify(
      cr === "ok"
        ? { key: `vq-check-${id}`, tone: "success", text: VENDOR_QUOTE_MSG.checkOk.replace("{note}", note ?? ""), testId: "vq-check-notice" }
        : { key: `vq-check-${id}`, tone: "warn", text: VENDOR_QUOTE_MSG.checkMismatch.replace("{note}", note ?? ""), testId: "vq-check-notice" },
    );
  }, [id, q.data, cr, st]);

  /** 答えを画面に入れ、状態・見積依頼が変わったら知らせる */
  const take = (out: Saved, before: VendorQuoteRow | null, msg?: string) => {
    qc.setQueryData(["vendor-quote", id], out);
    invalidateList(qc);
    void qc.invalidateQueries({ queryKey: ["vq-nb"] });
    void qc.invalidateQueries({ queryKey: ["vendor-quotes"] });
    const after = out.row;
    if (msg) notify(t(msg));
    if (before && after && !before.rfq && after.rfq) notify(VENDOR_QUOTE_LIST_MSG.rfqLinked.replace("{no}", after.rfq.displayNo ?? ""));
    if (before && after && (before.state !== after.state || before.partial !== after.partial)) {
      notify({ text: VENDOR_QUOTE_LIST_MSG.stateChanged.replace("{from}", vendorQuoteStateLabel(before.state, before.partial)).replace("{to}", vendorQuoteStateLabel(after.state, after.partial)), tone: "info", key: `vql-state-${id}` });
    }
  };
  /** 頭の直し（版つき）。ずれていたら読み直して同じ直しをもう一度（直すのは送った欄だけ） */
  const put = async (patch: Record<string, unknown>): Promise<void> => {
    const before = rowRef.current;
    const send = (version: string | undefined) => api<Saved>("PUT", `/vendor-quotes/${id}`, { ...patch, version });
    try {
      take(await send(before?.updatedAt), before);
    } catch (e) {
      if (e instanceof ApiError && e.code === "VENDOR_QUOTE_STALE") {
        const fresh = await q.refetch();
        take(await send(fresh.data?.row?.updatedAt), before);
        return;
      }
      throw e;
    }
  };
  const putNow = (patch: Record<string, unknown>) => {
    setBusy(true);
    put(patch)
      .catch((e) => notify({ text: messageOf(e), tone: "error" }))
      .finally(() => setBusy(false));
  };
  // 件名と税抜合計（手入力）：打つ欄は共通の自動保存（#177）。1本ずつ・送れていない直しは離れるときに確かめる
  const [title, setTitle] = useState("");
  const titleRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (titleRef.current === undefined) setTitle(q.data?.row?.title ?? "");
  }, [q.data?.row?.title]);
  const totalRef = useRef<number | null | undefined>(undefined);
  const [totalDraft, setTotalDraft] = useState<number | null | undefined>(undefined);
  const sq = useSaveQueue({
    send: async () => {
      const patch: Record<string, unknown> = {};
      const sentTitle = titleRef.current;
      const sentTotal = totalRef.current;
      if (sentTitle !== undefined) patch.title = sentTitle;
      if (sentTotal !== undefined) patch.totalExTax = sentTotal;
      if (!Object.keys(patch).length) return;
      await put(patch);
      if (titleRef.current === sentTitle) titleRef.current = undefined;
      if (totalRef.current === sentTotal) {
        totalRef.current = undefined;
        setTotalDraft(undefined);
      }
    },
  });
  /** 状態を変える操作（POST・DELETE・PUT）。答えの文を通知に、「元に戻す」があれば付ける */
  const act = async (method: "POST" | "DELETE" | "PUT", path: string, body?: unknown, undo?: { label: string; run: () => Promise<void> }) => {
    const before = rowRef.current;
    setBusy(true);
    try {
      const out = await api<Saved>(method, `/vendor-quotes/${id}${path}`, body);
      take(out, before);
      if (out.message) notify({ text: t(out.message), action: undo ? { label: undo.label, onClick: () => void undo.run() } : undefined });
    } catch (e) {
      notify({ text: messageOf(e), tone: "error" });
    } finally {
      setBusy(false);
    }
  };
  const saveLines = (lines: VendorQuoteLine[]) =>
    act("PUT", "/lines", { lines: lines.map((l) => ({ id: l.id, role: l.role, name: l.name, spec: l.spec, quantity: l.quantity, unit: l.unit, unitPrice: l.unitPrice, amount: l.amount, note: l.note, remarks: l.remarks })) });
  /** 削除：消したら一覧へ移り、そこで「元に戻す」つきの通知（一覧の画面が state.vqDeleted を見て出す） */
  const doDelete = async () => {
    setBusy(true);
    try {
      const out = await api<{ message: string }>("DELETE", `/vendor-quotes/${id}`);
      setDlg(null);
      invalidateList(qc);
      void qc.invalidateQueries({ queryKey: ["vendor-quotes"] });
      nav(`/vendor-quotes${listSearch}`, { state: { vqDeleted: { id, message: out.message } } });
    } catch (e) {
      notify({ text: messageOf(e), tone: "error" });
    } finally {
      setBusy(false);
    }
  };
  /** {order}を作成（回答の行を写す）→ その{order}の作成画面へ。作成済みの{order}があれば確かめてから（#233 2a。止めない） */
  const [askOrder, setAskOrder] = useState(false);
  const createOrder = async (confirmed = false) => {
    if (!confirmed && (row?.orders.length ?? 0) > 0) {
      setAskOrder(true);
      return;
    }
    setAskOrder(false);
    try {
      const o = await api<Order>("POST", "/orders", { fromVendorQuoteId: id });
      void qc.invalidateQueries({ queryKey: ["orders"] });
      nav(`/orders/${o.id}`, { state: { notice: t(ORDER_MSG.createdFromVendorQuote).replace("{no}", q.data?.quoteNo ?? ""), noticeTestId: "order-created-notice" } });
    } catch (e) {
      notify({ text: messageOf(e), tone: "error" });
    }
  };
  /** 採用／不採用（#233 2a。印だけ・確かめの窓なし・通知の「元に戻す」）。回答の比較の行（ほかの回答）も同じ口 */
  const setAdoption = async (targetId: string, next: VendorQuoteAdoption | null, prev: VendorQuoteAdoption | null) => {
    if (targetId === id) {
      await act("PUT", "/adoption", { adoption: next }, { label: ORDER_BUNDLE_MSG.undo, run: () => act("PUT", "/adoption", { adoption: prev }) });
      return;
    }
    setBusy(true);
    try {
      const out = await api<Saved>("PUT", `/vendor-quotes/${targetId}/adoption`, { adoption: next });
      void q.refetch();
      invalidateList(qc);
      const undo = () => void api("PUT", `/vendor-quotes/${targetId}/adoption`, { adoption: prev }).then(() => q.refetch(), (e) => notify({ text: messageOf(e), tone: "error" }));
      if (out.message) notify({ text: t(out.message), action: { label: ORDER_BUNDLE_MSG.undo, onClick: undo } });
    } catch (e) {
      notify({ text: messageOf(e), tone: "error" });
    } finally {
      setBusy(false);
    }
  };
  const go = (to: string | null) => {
    if (to) nav(`/vendor-quotes/${to}${listSearch}`, { replace: true });
  };

  // 結果の枠の幅で列を隠す（§13.5 ユ）
  const resBox = useRef<HTMLElement>(null);
  const [resW, setResW] = useState(1000);
  useEffect(() => {
    const el = resBox.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setResW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [q.isSuccess]);
  const onPage = useCallback(() => undefined, []);

  if (q.isError) {
    return (
      <div className="vqd" data-testid="vqd">
        <div className="vqd-head">
          <BackButton back={back} />
          <ErrorText>{messageOf(q.error)}</ErrorText>
        </div>
      </div>
    );
  }
  const v = q.data;
  if (!v || !row) return <div className="vqd" aria-busy="true" data-testid="vqd" />;

  const used = row.usage.done > 0 || v.status === "done";
  const reading = v.status === "queued" || v.status === "reading";
  const manual = v.status === "manual";
  const editable = v.status === "review" || manual;
  const jobs = (group.data?.jobs ?? []).filter((j) => j.status !== "lost");
  const canImport = !!row.group && !reading && row.state !== "rejected" && v.status !== "failed";
  const importTitle = !row.group ? t(VENDOR_QUOTE_LIST_MSG.importNeedsGroup) : !jobs.length && group.data ? t(VENDOR_QUOTE_MSG.noEstimate) : undefined;
  const importTo = (docId: string) => `/estimates/${docId}?vendorImport=${id}`;
  const why = reasonText(row);
  const name = `${row.vendor?.name ?? row.issuerName ?? L.vendor} ${titleOf(row)}`;
  // 選ぶ欄の候補（今の値は使わないにしていても出す）
  const vendorOpts = (vendors.data?.items ?? []).map((p) => ({ id: p.id, name: p.shortName || p.name }));
  if (row.vendor && !vendorOpts.some((x) => x.id === row.vendor!.id)) vendorOpts.unshift({ id: row.vendor.id, name: row.vendor.name });
  const groupOpts = (groups.data?.items ?? []).map((g) => ({ id: g.id, name: g.name }));
  if (row.group && !groupOpts.some((g) => g.id === row.group!.id)) groupOpts.unshift(row.group);
  const userOpts = [...(filters.data?.users ?? [])];
  if (row.assignee && !userOpts.some((u) => u.id === row.assignee!.id)) userOpts.unshift(row.assignee);

  // #233 2a：{order}を作成（モジュール sales・{vendor}が決まっている・PC）。採用なら主ボタン、ほかは「⋯」
  const canOrder = !!me.data?.tenant.modules.includes("sales") && !!row.vendor && !reading && !pcOnly.hidden("/orders/new");
  const adopted = row.adoption === "adopted";
  const declined = row.adoption === "declined";
  const menu: RowMenuItem[] = [
    ...(row.check === "mismatch"
      ? [row.checkAcked ? { label: M.unack, testId: "vqd-unack", onSelect: () => void act("DELETE", "/check-ack") } : { label: M.ack, testId: "vqd-ack", onSelect: () => void act("POST", "/check-ack") }]
      : []),
    ...(v.status === "review" ? [{ label: M.toManual, testId: "vqd-to-manual", onSelect: () => void act("POST", "/manual") }] : []),
    ...(v.status === "failed" || v.status === "review" ? [{ label: M.retry, testId: "vqd-retry", onSelect: () => void act("POST", "/retry") }] : []),
    ...(row.rfq ? [{ label: M.unlinkRfq, testId: "vqd-unrfq", onSelect: () => void act("PUT", "/rfq", { rfqId: null }) }] : []),
    ...(v.status === "done" ? [{ label: M.unconfirm, testId: "vqd-unconfirm", onSelect: () => void act("POST", "/unconfirm") }] : []),
    // #233（docs/93 §4.2）：回答の行を写して{order}を作成。採用の回答は主ボタン（#233 2a）なので「⋯」に出さない
    ...(canOrder && !adopted ? [{ label: t(ORDER_MSG.fromVendorQuote), divider: true, testId: "vqd-order", onSelect: () => void createOrder() }] : []),
    { label: M.openOriginal, divider: true, testId: "vqd-ext", onSelect: () => void window.open(v.original.url, "_blank", "noopener") },
    { label: M.delete, danger: true, divider: true, testId: "vqd-delete", disabled: used, ...(used ? { note: VENDOR_QUOTE_LIST_MSG.deleteBlocked } : {}), onSelect: () => setDlg("delete") },
  ];
  const saveText = sq.state.kind === "saving" ? M.saving : sq.state.kind === "saved" ? M.savedAt.replace("{at}", sq.state.at) : sq.state.kind === "error" ? `⚠ ${sq.state.msg}` : "";
  const n = nb.data;
  const widen = sp.split <= 50;
  const widenLabel = widen ? M.widenOriginal : M.widenResult;
  /** 検算の結果があるときだけ「✓ 検算一致」などを出す（無いとき「—」を並べない） */
  const hasCheck = row.check === "ok" || row.check === "mismatch" || row.check === "failed";

  return (
    <div className="vqd" data-testid="vqd">
      {/* 頭の帯（2段＋詳細） */}
      <div className="vqd-head" data-testid="vqd-head">
        <div className="vqd-r1">
          <div className="vqd-tcol">
            <BackButton back={back} />
            <div className="vqd-trow">
              <h1 className={"vqd-name" + (row.vendor ? "" : " vql-unset")} data-testid="vqd-vendor">
                {row.vendor?.name ?? `${L.vendor} ${VENDOR_QUOTE_LIST_MSG.unset}`}
              </h1>
              <StateChip r={row} sub={false} />
              {hasCheck ? <span className="vqd-check"><ReadCell r={row} /></span> : null}
              <span className="vqd-sub" title={titleOf(row)}>
                {titleOf(row)}
                {row.quoteNo ? `　${row.quoteNo}` : ""}
                {row.quotedOn ? `　${row.quotedOn.replace(/-/g, "/")}` : ""}
              </span>
            </div>
          </div>
          <span className="sp" />
          <span className="vql-saved" aria-live="polite">{saveText}</span>
          <div className="vqd-pager" role="group" aria-label="前へ・次へ" data-testid="vqd-pager">
            <button type="button" className="btn text" disabled={!n?.prev} title={t(M.prevTitle)} onClick={() => go(n?.prev ?? null)} data-testid="vqd-prev">
              ‹ {M.prev}
            </button>
            <span className="num vqd-pos" data-testid="vqd-pos">{n && n.index ? M.position.replace("{i}", String(n.index)).replace("{n}", String(n.total)) : n ? `— ／ ${n.total}` : ""}</span>
            <button type="button" className="btn text" disabled={!n?.next} title={t(M.nextTitle)} onClick={() => go(n?.next ?? null)} data-testid="vqd-next">
              {M.next} ›
            </button>
          </div>
          {/* #233 2a：採用／不採用（印だけ）。不採用なら任意の「辞退連絡」（主ボタンにしない） */}
          <AdoptionToggle value={row.adoption} used={used} busy={busy} onChange={(x) => void setAdoption(id, x, row.adoption)} testId="vqd-adoption" />
          {declined && row.vendor ? (
            <button type="button" className="btn" disabled={busy} title={v.declineSent ? ORDER_BUNDLE_MSG.declineSentAt.replace("{md}", md(v.declineSent.at.slice(0, 10))) : undefined} onClick={() => setDlg("decline")} data-testid="vqd-decline-mail">
              {ORDER_BUNDLE_MSG.declineMail}
              {v.declineSent ? <span className="muted num vqd-sentmd" data-testid="vqd-decline-sent">{` ${md(v.declineSent.at.slice(0, 10))}`}</span> : null}
            </button>
          ) : null}
          {jobs.length > 1 && canImport ? (
            <RowMenu label={VENDOR_QUOTE_LIST_MSG.importPick} testId="vqd-import" trigger={VENDOR_QUOTE_LIST_MSG.importBtn} triggerClass={adopted && canOrder ? "btn" : "btn primary"} items={jobs.map((j) => ({ label: `${j.displayNo ?? "（下書き）"} ${j.subject}`, to: importTo(j.id) }))} />
          ) : (
            <button type="button" className={adopted && canOrder ? "btn" : "btn primary"} disabled={!canImport || !jobs.length} title={importTitle} onClick={() => jobs[0] && nav(importTo(jobs[0].id))} data-testid="vqd-import">
              {VENDOR_QUOTE_LIST_MSG.importBtn}
            </button>
          )}
          {adopted && canOrder ? (
            <button type="button" className="btn primary" disabled={busy} onClick={() => void createOrder()} data-testid="vqd-order-primary">
              {t(ORDER_MSG.fromVendorQuote)}
            </button>
          ) : null}
          <RowMenu label={M.more} items={menu} testId="vqd-more" trigger={DOTS_H} triggerClass="vql-ibtn vqd-morebtn" />
        </div>
        <div className="vqd-r2">
          <label className={"vql-fld vqd-f" + (row.vendor ? "" : " is-unset")}>
            <span>{L.vendor}</span>
            <select value={row.vendor?.id ?? ""} disabled={busy} onChange={(e) => putNow({ vendorId: e.target.value || null })} data-testid="vqd-f-vendor">
              <option value="">{VENDOR_QUOTE_LIST_MSG.unset}</option>
              {vendorOpts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </label>
          <label className={"vql-fld vqd-f is-group" + (row.group ? "" : " is-unset")} title={used ? VENDOR_QUOTE_LIST_MSG.groupLocked : !row.group ? t(VENDOR_QUOTE_LIST_MSG.importNeedsGroup) : undefined}>
            <span>{L.project}</span>
            <select value={row.group?.id ?? ""} disabled={busy || used} onChange={(e) => putNow({ groupId: e.target.value || null })} data-testid="vqd-f-group">
              <option value="">{VENDOR_QUOTE_LIST_MSG.unset}</option>
              {groupOpts.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </label>
          <label className="vql-fld vqd-f is-user">
            <span>担当者</span>
            <select value={row.assignee?.id ?? ""} disabled={busy} onChange={(e) => putNow({ assigneeId: e.target.value || null })} data-testid="vqd-f-assignee">
              <option value="">{VENDOR_QUOTE_LIST_MSG.unset}</option>
              {userOpts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
          <span className="vqd-div" aria-hidden="true" />
          <Kv k={M.exTax} big testId="vqd-ex">{yenOrDash(row.totalExTax)}</Kv>
          <Kv k={M.taxAmount}>{yenOrDash(row.taxAmount)}</Kv>
          <Kv k={M.incTax} big testId="vqd-inc">{yenOrDash(row.totalIncTax)}</Kv>
          <span className="vqd-div" aria-hidden="true" />
          <Kv k={M.usage} testId="vqd-usage">
            {row.usage.all ? `${row.usage.done}／${row.usage.all}行` : "—"}
            {row.usage.documents.map((d) => (
              <Link key={d.id} className="vqd-uselink" to={`/estimates/${d.id}`} title={`${d.displayNo ?? "（下書き）"} ${d.subject}`}>
                {" → "}
                {d.displayNo ?? "（下書き）"}
              </Link>
            ))}
          </Kv>
          <span className="sp" />
          <button type="button" className="btn text vqd-toggle" aria-expanded={open} aria-label={open ? M.detailsClose : M.detailsOpen} onClick={() => setOpen(!open)} data-testid="vqd-toggle">
            {M.details} <span className={"vqd-chev" + (open ? " is-open" : "")} aria-hidden="true" />
          </button>
        </div>
        {open ? (
          <div className="vqd-r3" data-testid="vqd-details">
            {why && (row.state === "review" || row.state === "rejected") ? (
              <div className={"vqd-why" + (row.state === "rejected" ? " is-plain" : "")} data-testid="vqd-why">
                {row.state === "review" ? ICON.warn : null}
                {why}
              </div>
            ) : null}
            <label className="vql-fld vqd-f is-title">
              <span>{M.title}</span>
              <input
                value={title}
                maxLength={200}
                placeholder={row.fileName ?? ""}
                onChange={(e) => {
                  setTitle(e.target.value);
                  titleRef.current = e.target.value;
                  sq.mark();
                }}
                onBlur={() => void sq.flush()}
                data-testid="vqd-f-title"
              />
            </label>
            <label className="vql-fld vqd-f is-no">
              <span>{M.quoteNo}</span>
              <input defaultValue={v.quoteNo} disabled={!editable || busy} onBlur={(e) => e.target.value !== v.quoteNo && putNow({ quoteNo: e.target.value })} data-testid="vqd-f-no" />
            </label>
            <div className="vql-fld vqd-f is-date">
              <span>{M.quotedOn}</span>
              {editable ? (
                <DateInput label={M.quotedOn} ariaLabel={M.quotedOn} value={v.quotedOn ?? ""} onChange={(x) => (x || null) !== v.quotedOn && putNow({ quotedOn: x || null })} testId="vqd-f-date" />
              ) : (
                <b className="num vqd-val">{v.quotedOn ? v.quotedOn.replace(/-/g, "/") : "—"}</b>
              )}
            </div>
            <div className="vql-fld vqd-f is-tax">
              <span>{M.tax}</span>
              {editable ? (
                <div className="seg" role="group" aria-label={M.tax} data-testid="vqd-f-tax">
                  <button type="button" aria-pressed={!v.taxIncluded} disabled={busy} onClick={() => v.taxIncluded && putNow({ taxIncluded: false })}>{M.taxEx}</button>
                  <button type="button" aria-pressed={v.taxIncluded} disabled={busy} onClick={() => !v.taxIncluded && putNow({ taxIncluded: true })}>{M.taxIn}</button>
                </div>
              ) : (
                <b className="vqd-val">{v.taxIncluded ? M.taxIn : M.taxEx}</b>
              )}
            </div>
            {manual ? (
              <label className="vql-fld vqd-f is-total">
                <span>{M.totalManual}</span>
                <NumInput
                  kind="int"
                  className="num"
                  value={totalDraft !== undefined ? totalDraft : v.totalExTax}
                  onChange={(x) => {
                    totalRef.current = x;
                    setTotalDraft(x);
                    sq.mark();
                  }}
                  onBlur={() => void sq.flush()}
                  aria-label={M.totalManual}
                  data-testid="vqd-f-total"
                />
              </label>
            ) : null}
            <Kv k={M.rfq} testId="vqd-rfq">
              {row.rfq ? (
                <>
                  <Link to={`/rfqs/${row.rfq.id}`}>依頼 {row.rfq.displayNo}</Link>
                  <span className="muted">（{row.rfq.status === "answered" ? "回答あり" : "回答待ち"}）</span>
                </>
              ) : v.rfqCandidates.length ? (
                <select className="vqd-rfqpick" value="" disabled={busy} onChange={(e) => e.target.value && void act("PUT", "/rfq", { rfqId: e.target.value })} aria-label={M.rfq} data-testid="vqd-rfq-pick">
                  <option value="">{VENDOR_QUOTE_LIST_MSG.rfqCandidates.replace("{n}", String(v.rfqCandidates.length))}</option>
                  {v.rfqCandidates.map((c) => <option key={c.id} value={c.id}>{c.displayNo}　{c.subject}{c.groupName ? `（${c.groupName}）` : ""}</option>)}
                </select>
              ) : (
                "—"
              )}
            </Kv>
            <Kv k={M.received}>
              <span className="num">{receivedTitle(row.receivedAt)}</span>
              {row.registeredBy ? `（${row.registeredBy.name}）` : ""}・{v.sourceLabel}
              {v.pageCount ? `・${v.pageCount}ページ` : ""}
            </Kv>
          </div>
        ) : null}
        {/* #233 2a：回答の比較（同じ{project}・同じ件名の見積依頼への回答が2件以上） */}
        <CompareBand items={v.compare} busy={busy} onChange={(x, nx, pv) => void setAdoption(x, nx, pv)} />
      </div>

      {/* 原本 ｜ 境 ｜ AI読み取り結果 */}
      <div ref={sp.bodyRef} className={"vqd-body is-cols" + (sp.dragging ? " is-dragging" : "")} style={{ "--vqd-split": `${sp.split}%` } as CSSProperties} data-testid="vqd-body">
        <section className="vqd-orig" aria-label={VENDOR_QUOTE_LIST_MSG.original}>
          <PageViewer
            docKey={id}
            title={titleOf(row)}
            pages={pages.data?.pages}
            error={pages.isError ? pages.error : undefined}
            pageCount={v.pageCount}
            openUrl={v.original.url}
            zoom={zoom}
            onZoom={setZoom}
            onPage={onPage}
            jump={jump}
            label={VENDOR_QUOTE_LIST_MSG.original}
            ids={{ root: "vqd-original", toolbar: "vqd-otool", zoomOut: "vqd-zoom-out", zoom: "vqd-zoom", zoomIn: "vqd-zoom-in", fit: "vqd-fit", page: "vqd-page", open: "vqd-open", pages: "vqd-pages", preparing: "vqd-preparing", img: "vqd-page-img" }}
            extra={
              <button type="button" className="rdp-tb rdp-ico" aria-label={widenLabel} title={widenLabel} onClick={() => sp.put(widen ? 65 : 35)} data-testid="vqd-widen" data-side={widen ? "original" : "result"}>
                <WidenIcon side={widen ? "original" : "content"} />
              </button>
            }
          />
        </section>
        <div className="rdr-grip" {...sp.gripProps(true)} aria-label={t(M.splitter.replace("{readResult}", VENDOR_QUOTE_LIST_MSG.readResult))} data-testid="vqd-split">
          <span className="rdr-griphandle" aria-hidden="true" />
          {sp.dragging ? (
            <span className="vqd-tip" role="status">
              {VENDOR_QUOTE_LIST_MSG.original} {sp.split}%　結果 {100 - sp.split}%
            </span>
          ) : null}
        </div>
        <section ref={resBox} className={"vqd-res" + (resW < TIGHT_PX ? " is-tight" : resW < NARROW_PX ? " is-narrow" : "")} aria-label={VENDOR_QUOTE_LIST_MSG.readResult} data-testid="vqd-result">
          <div className="vqd-rtool">
            <b>{VENDOR_QUOTE_LIST_MSG.readResult}</b>
            {v.lines.length ? <span className="muted">{M.rows.replace("{n}", String(v.lines.length))}</span> : null}
            <Help testId="help-vqd" label={`${VENDOR_QUOTE_LIST_MSG.readResult}の説明`} text={t(M.help)} />
            {reading || v.status === "failed" || !hasCheck ? null : <span className="vqd-check"><ReadCell r={row} /></span>}
            <span className="sp" />
          </div>
          <div className="vqd-rbody">
            <ResultBody
              cols={visibleCols(resW)}
              v={v}
              editable={editable}
              manual={manual}
              busy={busy}
              onSave={(lines) => void saveLines(lines)}
              onJump={(pn) => setJump({ n: pn, at: Date.now() })}
              onToManual={() => void act("POST", "/manual")}
              onRetry={() => void act("POST", "/retry")}
              onDelete={() => setDlg("delete")}
            />
          </div>
          {!reading && v.status !== "failed" && v.check.note ? (
            <div className="vqd-foot" data-testid="vqd-foot">
              <span className="muted">{v.check.note}</span>
            </div>
          ) : null}
        </section>
      </div>

      {dlg === "decline" ? (
        <DeclineMailDialog
          vendorQuoteId={id}
          onClose={() => setDlg(null)}
          onSent={() => {
            setDlg(null);
            notify({ text: ORDER_BUNDLE_MSG.declineSent, testId: "decline-sent-notice" });
            void q.refetch();
          }}
        />
      ) : null}
      {askOrder && row ? <AlreadyDialog refs={row.orders} busy={false} onOk={() => void createOrder(true)} onClose={() => setAskOrder(false)} /> : null}
      {dlg === "delete" ? <VendorQuoteDeleteDialog name={name} busy={busy} onCancel={() => setDlg(null)} onOk={() => void doDelete()} /> : null}
      {sq.dialog}
      {back.dialog}
    </div>
  );
}

function Kv({ k, children, big = false, testId }: { k: string; children: ReactNode; big?: boolean; testId?: string }) {
  return (
    <div className={"vqd-kv" + (big ? " is-big" : "")}>
      <span>{k}</span>
      <b className="num" data-testid={testId}>{children}</b>
    </div>
  );
}

/** 結果の枠の中身：読み取り中・読み取れず・表（修正の帯・行を追加） */
function ResultBody({
  cols,
  v,
  editable,
  manual,
  busy,
  onSave,
  onJump,
  onToManual,
  onRetry,
  onDelete,
}: {
  /** 見えている列の数（visibleCols）。帯の行はこの数だけ跨ぐ */
  cols: number;
  v: VendorQuote;
  editable: boolean;
  manual: boolean;
  busy: boolean;
  onSave: (lines: VendorQuoteLine[]) => void;
  onJump: (page: number) => void;
  onToManual: () => void;
  onRetry: () => void;
  onDelete: () => void;
}) {
  const [edit, setEdit] = useState<string | null>(null);
  if (v.status === "queued" || v.status === "reading") {
    return (
      <div className="vql-rdmsg" data-testid="vqd-reading">
        <b>{ICON.scan}{VENDOR_QUOTE_LIST_STATE_LABEL.reading}</b>
        <span>{v.status === "queued" ? M.queued : M.reading}</span>
        <span className="row">
          <button type="button" className="btn" disabled={busy} onClick={onToManual} data-testid="vqd-to-manual-btn">{M.toManual}</button>
          <button type="button" className="btn quiet" disabled={busy} onClick={onDelete} data-testid="vqd-cancel-read">{M.cancelRead}</button>
        </span>
      </div>
    );
  }
  if (v.status === "failed") {
    return (
      <div className="vql-rdmsg is-ng" data-testid="vqd-failed">
        <b>{ICON.warn}{VENDOR_QUOTE_MSG.failedBand}</b>
        <span>{VENDOR_QUOTE_MSG.failedHelp}</span>
        <span className="row">
          <button type="button" className="btn primary" disabled={busy} onClick={onToManual} data-testid="vqd-manual">{M.manual}</button>
          <button type="button" className="btn" disabled={busy} onClick={onRetry} data-testid="vqd-retry-btn">{M.retry}</button>
          <button type="button" className="btn quiet" disabled={busy} onClick={onDelete} data-testid="vqd-delete-btn">{M.delete}</button>
        </span>
      </div>
    );
  }
  const usedBy = new Map<string, VendorQuote["importedLines"]>();
  for (const x of v.importedLines) usedBy.set(x.vendorLineId, [...(usedBy.get(x.vendorLineId) ?? []), x]);
  const sheets = new Set(v.lines.map((l) => sheetLabel(l.sheetName)));
  let prevSheet = "";
  const rows: ReactNode[] = [];
  for (const l of v.lines) {
    const sh = sheetLabel(l.sheetName);
    if (sheets.size > 1 && sh !== prevSheet) rows.push(<tr key={`${l.id}-band`} className="is-band"><td colSpan={cols}>{sh}</td></tr>);
    prevSheet = sh;
    const sum = SUM.has(l.role);
    const head = l.role === "heading";
    const u = usedBy.get(l.id);
    const canEdit = editable;
    const open = edit === l.id && canEdit;
    rows.push(
      <tr
        key={l.id}
        className={(sum ? "is-sum" : head ? "is-head" : "") + (canEdit ? " is-edit" : "") + (open ? " is-open" : "")}
        tabIndex={canEdit ? 0 : undefined}
        aria-expanded={canEdit ? open : undefined}
        onClick={(e) => {
          if (!canEdit || (e.target as HTMLElement).closest("a,button")) return;
          setEdit(open ? null : l.id);
        }}
        onKeyDown={(e) => {
          if (canEdit && e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setEdit(open ? null : l.id);
          }
        }}
        data-testid="vqd-line"
        data-line={l.id}
      >
        <td className="num c-no">{sum || head ? "" : l.lineNo}</td>
        <td className="c-kind">{l.role === "adjustment" && l.amount != null && l.amount < 0 ? VENDOR_LINE_ROLE_LABEL.discount : VENDOR_LINE_ROLE_LABEL[l.role]}</td>
        <td className="c-name">
          <span className="vql-nm">{l.name}</span>
          {l.corrected ? <span className="chip vqd-fixed">{M.corrected}</span> : null}
          {l.spec ? <span className="vql-sp">{l.spec}</span> : null}
        </td>
        <td className="r num c-qty">{sum || l.quantity == null ? "" : l.quantity}</td>
        <td className="c-unit">{sum ? "" : l.unit}</td>
        <td className="r num c-price">{l.unitPrice == null ? "" : l.unitPrice.toLocaleString("ja-JP")}</td>
        <td className="r num c-amt">{l.amount == null ? (l.note || "") : l.amount.toLocaleString("ja-JP")}</td>
        <td className="c-used">
          {u ? (
            <b className="shu" title={u.map((x) => `${x.displayNo ?? "（下書き）"} ${x.lineNo} ${x.name || t("（{lineName}なし）")}`).join("・")} data-testid="vqd-line-used">
              {M.used}
            </b>
          ) : l.pageNo ? (
            <button type="button" className="btn text vqd-pjump" onClick={() => onJump(l.pageNo!)} aria-label={M.jump.replace("{n}", String(l.pageNo))}>
              P.{l.pageNo}
            </button>
          ) : null}
        </td>
      </tr>,
    );
    if (open) {
      rows.push(
        <tr key={`${l.id}-edit`} className="is-editrow">
          <td colSpan={cols}>
            <LineEditBand
              line={l}
              onSave={(nl) => {
                setEdit(null);
                onSave(v.lines.map((x) => (x.id === nl.id ? nl : x)));
              }}
              onCancel={() => setEdit(null)}
              onDelete={manual && !u ? () => { setEdit(null); onSave(v.lines.filter((x) => x.id !== l.id)); } : undefined}
            />
          </td>
        </tr>,
      );
    }
  }
  return (
    <>
      <table className="vqd-rd" data-testid="vqd-table">
        <colgroup>
          <col className="c-no" />
          <col className="c-kind" />
          <col className="c-name" />
          <col className="c-qty" />
          <col className="c-unit" />
          <col className="c-price" />
          <col className="c-amt" />
          <col className="c-used" />
        </colgroup>
        <thead>
          <tr>
            <th className="c-no">{M.colNo}</th>
            <th className="c-kind">{M.colKind}</th>
            <th className="c-name">{t(M.colName)}</th>
            <th className="r c-qty">{M.colQty}</th>
            <th className="c-unit">{M.colUnit}</th>
            <th className="r c-price">{M.colPrice}</th>
            <th className="r c-amt">{M.colAmount}</th>
            <th className="c-used">{M.colUsed}</th>
          </tr>
        </thead>
        <tbody>
          {rows}
          {!v.lines.length ? (
            <tr>
              <td colSpan={cols} className="muted">{M.noLines}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {manual ? <AddLineRow busy={busy} onAdd={(nl) => onSave([...v.lines, nl])} /> : null}
    </>
  );
}

/** 読み取った行を直す（元の文字は残る。前の中身の画面と同じ欄） */
function LineEditBand({ line, onSave, onCancel, onDelete }: { line: VendorQuoteLine; onSave: (l: VendorQuoteLine) => void; onCancel: () => void; onDelete?: (() => void) | undefined }) {
  const [f, setF] = useState<VendorQuoteLine>(line);
  const raw = line.rawText ?? `${line.name}　${line.quantity ?? ""} ${line.unit}　${line.unitPrice ?? ""}　${line.amount ?? ""}`.trim();
  return (
    <div className="vqedit" data-testid="vqd-edit-band">
      <label>{t("{lineName}")}<input className="inp n" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="vqd-f-name" autoFocus /></label>
      <label>{t("{lineSpec}")}<input className="inp n" value={f.spec} onChange={(e) => setF({ ...f, spec: e.target.value })} data-testid="vqd-f-spec" /></label>
      <label>数量<NumInput kind="dec" className="inp" value={f.quantity} onChange={(x) => setF({ ...f, quantity: x })} data-testid="vqd-f-qty" /></label>
      <label>単位<UnitInput className="inp u" value={f.unit} onChange={(x) => setF({ ...f, unit: x })} /></label>
      <label>単価<NumInput kind="int" className="inp" value={f.unitPrice} onChange={(x) => setF({ ...f, unitPrice: x })} data-testid="vqd-f-price" /></label>
      <label>金額<NumInput kind="int" className="inp" value={f.amount} onChange={(x) => setF({ ...f, amount: x })} data-testid="vqd-f-amount" /></label>
      <label>{t("{lineNote}")}<input className="inp n" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="vqd-f-remarks" /></label>
      <button type="button" className="small ai" onClick={() => onSave({ ...f, amount: f.amount ?? (f.quantity != null && f.unitPrice != null ? Math.round(f.quantity * f.unitPrice) : null) })} data-testid="vqd-f-ok">修正</button>
      <button type="button" className="btn text" onClick={onCancel}>取消</button>
      {onDelete ? <button type="button" className="btn text danger" onClick={onDelete}>行削除</button> : null}
      <span className="hint" style={{ flexBasis: "100%" }}>読み取った元の文字：{raw}</span>
    </div>
  );
}

/** 手入力：行を追加 */
function AddLineRow({ onAdd, busy }: { onAdd: (l: VendorQuoteLine) => void; busy: boolean }) {
  const [name, setName] = useState("");
  const [quantity, setQuantity] = useState<number | null>(1);
  const [unit, setUnit] = useState("式");
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [err, setErr] = useState<string | undefined>();
  const nameRef = useRef<HTMLInputElement>(null);
  const add = () => {
    if (!name.trim()) return setErr(t("{lineName}を入力してください"));
    const a = amount ?? (quantity != null && unitPrice != null ? Math.round(quantity * unitPrice) : null);
    if (a == null) return setErr("金額（か 数量と単価）を入力してください");
    setErr(undefined);
    onAdd({ id: crypto.randomUUID(), lineNo: 0, role: "detail", name: name.trim(), spec: "", quantity, unit, unitPrice, amount: a, note: "", remarks: "", rawText: null, pageNo: null, sheetName: null, mappedLineageId: null, mappedBy: null, mappedConfidence: null, corrected: true, keepOwnCost: false });
    setName("");
    setQuantity(1);
    setUnit("式");
    setUnitPrice(null);
    setAmount(null);
    nameRef.current?.focus();
  };
  return (
    <div className="vqadd vqd-add" data-testid="vqd-add">
      <label>{t("{lineName}")}<input ref={nameRef} className="inp n" value={name} onChange={(e) => setName(e.target.value)} data-testid="vqd-add-name" /></label>
      <label>数量<NumInput kind="dec" className="inp" value={quantity} onChange={setQuantity} /></label>
      <label>単位<UnitInput className="inp u" value={unit} onChange={setUnit} /></label>
      <label>単価<NumInput kind="int" className="inp" value={unitPrice} onChange={setUnitPrice} /></label>
      <label>金額<NumInput kind="int" className="inp" value={amount} onChange={setAmount} data-testid="vqd-add-amount" /></label>
      <button type="button" className="small ai" disabled={busy} onClick={add} data-testid="vqd-add-btn">{M.addLine}</button>
      <ErrorText>{err}</ErrorText>
    </div>
  );
}
